import type { FunnelParams, FunnelParamsPatch } from '@/model/types';

/**
 * The seam between the canvas and wherever params live.
 *
 * The app never imports an adapter directly — it takes a port. Swapping
 * localStorage for the API service is one line in `index.ts`, and the model's
 * `mergeParams` guarantees a partial payload merges rather than clobbering.
 */

/**
 * The outcome of a load, which is deliberately three-valued rather than
 * `params | null`.
 *
 * The distinction that matters is `absent` versus `unavailable`. Collapsing them
 * into one "nothing here" answer makes it impossible to tell "this code does not
 * exist" from "I could not ask" — and those need opposite handling. A service
 * that says a code is unknown should stop the canvas; a service that cannot be
 * reached must never stop it, because losing the tool mid-meeting is far worse
 * than opening a scenario that turns out to be unrecognized.
 */
export type LoadResult =
  /** The scenario exists. `params` may be sparse; mergeParams fills the rest. */
  | { status: 'found'; params: FunnelParamsPatch }
  /** The store was reached and holds no such scenario. A definite answer. */
  | { status: 'absent' }
  /** The store could not be reached. Not an answer — callers must fail open. */
  | { status: 'unavailable' };

export interface PersistencePort {
  readonly kind: 'local' | 'http';
  load(scenarioId: string): Promise<LoadResult>;
  save(scenarioId: string, params: FunnelParams): Promise<void>;
  /**
   * Best-effort write during pagehide. Must not await — the page is going away.
   * Implementations use sendBeacon or a synchronous store.
   */
  flush(scenarioId: string, params: FunnelParams): void;
}

export class PersistenceError extends Error {
  constructor(
    message: string,
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'PersistenceError';
  }
}
